import './index.scss'

import { useEffect, useState } from 'react'

import LoadingIndicator from '@components/LoadingIndicator'
import Modal from '@components/Modal'
import { useChurchDataProvider } from '@data/ChurchDataProvider'
import { useSeriesList } from '@hooks/useSeries'
import { ChurchSeries } from '@interfaces/series.interface'
import SeriesEditorModal from '../SeriesEditorModal'

import checkmarkIcon from 'url:@icon/checkmark.svg';
import closeIcon from 'url:@icon/close.svg';
import plusIcon from 'url:@assets/icons/plusNoCircle.svg';

// Chooses a service's series from its campus and category, a page at a time,
// or starts a new one there. A pick is held until it's confirmed with the
// checkmark; a series created here is chosen straight away. Nothing loads
// until it's open.
export default function SeriesPickerModal(props: {
  isOpen: boolean,
  campusID: string,
  categoryID: string | null,
  selectedSeriesID: string | null,
  close: () => void,
  // Null takes the service out of its series.
  onSelect: (series: ChurchSeries | null) => void
}) {
  const { campuses, categories } = useChurchDataProvider();

  const { series, loading, hasLoaded, error, hasMore, isLoadingMore, loadMore, refetch } = useSeriesList(
    props.isOpen ? props.campusID : null,
    props.categoryID
  );

  // What's been picked but not yet confirmed. `series` is kept alongside the
  // ID so the caller can show its name; it's null for "No Series" and for
  // the service's current series, which the caller already knows.
  const [pending, setPending] = useState<{ id: string | null; series: ChurchSeries | null }>({
    id: props.selectedSeriesID,
    series: null
  });

  // Each open starts from the service's series as it is now.
  useEffect(() => {
    if (props.isOpen) {
      setPending({ id: props.selectedSeriesID, series: null });
    }
  }, [props.isOpen, props.selectedSeriesID]);

  const hasChanged = pending.id !== props.selectedSeriesID;

  // Keyed per open, so each new series starts blank.
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editorKey, setEditorKey] = useState(0);

  const campusName = campuses.find((campus) => campus.id === props.campusID)?.attributes.name ?? 'This campus';
  const categoryName = categories.find((category) => category.id === props.categoryID)?.name ?? 'No Category';

  let list;

  if (loading && !hasLoaded) {
    list = <div className='series-picker-state'>
      <LoadingIndicator fullScreen={false} delayMs={250} />
    </div>
  } else if (error && series.length === 0) {
    list = <div className='series-picker-state'>
      <p>{error}</p>
      <button type='button' className='text-button' onClick={() => void refetch()}>Try Again</button>
    </div>
  } else {
    list = <>
      <ul className='series-picker-list'>
        <SeriesOption
          name='No Series'
          isSelected={pending.id === null}
          onClick={() => setPending({ id: null, series: null })} />

        {series.map((item) => <SeriesOption
          key={item.id}
          name={item.attributes.name || 'Untitled Series'}
          thumbnailURL={item.attributes.thumbnailURL}
          isSelected={item.id === pending.id}
          onClick={() => setPending({ id: item.id, series: item })} />)}
      </ul>

      {series.length === 0 ? <p className='series-picker-empty'>
        There aren't any series here yet. Start one below.
      </p> : null}

      {error ? <p className='series-picker-error'>{error}</p> : null}

      {hasMore ? <button type='button' className='text-button load-more' disabled={isLoadingMore} onClick={loadMore}>
        {isLoadingMore ? 'Loading…' : 'Load More'}
      </button> : null}
    </>
  }

  return <>
    <Modal
      id='series-picker-modal'
      isOpen={props.isOpen}
      footerButtons={[]}>
      <div className='series-picker-modal-content'>
        <div className='header'>
          <button type='button' className='header-control close' onClick={props.close}>
            <img src={closeIcon} alt='Close icon' />
          </button>

          <h3 className='title'>Choose a Series</h3>

          <button
            type='button'
            className='header-control confirm'
            disabled={!hasChanged}
            onClick={() => props.onSelect(pending.series)}>
            <img src={checkmarkIcon} alt='Confirm selection' />
          </button>
        </div>

        <p className='caption'>Showing Series in {campusName} · {categoryName}</p>

        {list}

        <button
          type='button'
          className='new-series-button'
          onClick={() => {
            setEditorKey((current) => current + 1);
            setIsEditorOpen(true);
          }}>
          <img src={plusIcon} alt='' aria-hidden='true' />
          New Series
        </button>
      </div>
    </Modal>

    {/* Opens over the picker, starting where the service is filed. */}
    <SeriesEditorModal
      key={`picker-new-series-${editorKey}`}
      isOpen={isEditorOpen}
      initialCampusID={props.campusID}
      initialCategoryID={props.categoryID}
      close={() => setIsEditorOpen(false)}
      onSaved={(created) => {
        setIsEditorOpen(false);
        props.onSelect(created);
      }} />
  </>
}

function SeriesOption(props: {
  name: string;
  thumbnailURL?: string | null;
  isSelected: boolean;
  onClick: () => void;
}) {
  return <li>
    <button
      type='button'
      className={props.isSelected ? 'series-option is-selected' : 'series-option'}
      aria-pressed={props.isSelected}
      onClick={props.onClick}>
      {props.thumbnailURL !== undefined ? <span className='series-option-thumbnail'>
        {props.thumbnailURL ? <img src={props.thumbnailURL} alt='' aria-hidden='true' /> : null}
      </span> : null}

      <span className='series-option-name'>{props.name}</span>

      {/* Drawn inline so it takes the accent color; the shared checkmark icon
          is light grey. */}
      {props.isSelected ? <svg className='series-option-check' viewBox='0 0 16 16' role='img' aria-label='Selected'>
        <path d='M3 8.5l3.2 3.2L13 5' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
      </svg> : null}
    </button>
  </li>
}
